import { fetchApi } from './api';
import type {
    ConsBusinessRow, ConsBusinessStats, ConsBusinessFilterOptions, ConsBusinessFilters,
    ConsBusinessGroupDetail, ConsBusinessSortField, SortOrder, KpiMode,
    StatusSetPayload, StatusSetResult, ConsBusinessHistoryEvent, ConsBusinessConfig,
    ConsBusinessComment, ConsBusinessCommentArm,
    ConsBusinessTransactionsResponse,
} from '../types/consolidatedBusiness';
import type {
    ForwardComposeData, ForwardSendPayload, MailSendResult, MailHistoryItem,
} from '../types/consBusinessMail';

interface PaginatedResponse<T> {
    items: T[];
    total: number;
}

interface ListParams {
    page: number;
    limit: number;
    sortBy: ConsBusinessSortField;
    sortOrder: SortOrder;
    kpiMode: KpiMode;
    filters: ConsBusinessFilters;
}

function applyFilters(sp: URLSearchParams, filters: ConsBusinessFilters, kpiMode: KpiMode): void {
    sp.set('kpiMode', kpiMode);
    if (filters.companyCode) sp.set('companyCode', filters.companyCode);
    if (filters.businessUnit) sp.set('businessUnit', filters.businessUnit);
    if (filters.salesManager) sp.set('salesManager', filters.salesManager);
    if (filters.salesTeam) sp.set('salesTeam', filters.salesTeam);
    if (filters.supplierName) sp.set('supplierName', filters.supplierName);
    if (filters.categoryName) sp.set('categoryName', filters.categoryName);
    if (filters.search) sp.set('search', filters.search);
    if (filters.historyMonths) sp.set('historyMonths', filters.historyMonths);
    if (filters.monthsSinceLastInvoice) sp.set('monthsSinceLastInvoice', filters.monthsSinceLastInvoice);
    if (filters.minSales) sp.set('minSales', filters.minSales);
    if (filters.onlyOverdue) sp.set('onlyOverdue', '1');
    if (filters.status) sp.set('status', filters.status);
    if (filters.hideLost) sp.set('hideLost', '1');
}

export const consolidatedBusinessApi = {
    async list(params: ListParams): Promise<PaginatedResponse<ConsBusinessRow>> {
        const sp = new URLSearchParams();
        sp.set('page', String(params.page));
        sp.set('limit', String(params.limit));
        sp.set('sortBy', params.sortBy);
        sp.set('sortOrder', params.sortOrder);
        applyFilters(sp, params.filters, params.kpiMode);

        // Raw fetch (not fetchApi) so we can read the pagination wrapper for total.
        const response = await fetch(`/api/v1/consolidated-business?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const body = await response.json();
        if (!response.ok || !body.success) {
            throw new Error(body.error?.message || `API error: ${response.status}`);
        }
        return {
            items: body.data as ConsBusinessRow[],
            total: body.pagination?.total ?? 0,
        };
    },

    getStats(filters: ConsBusinessFilters, kpiMode: KpiMode): Promise<ConsBusinessStats> {
        const sp = new URLSearchParams();
        applyFilters(sp, filters, kpiMode);
        const qs = sp.toString();
        return fetchApi<ConsBusinessStats>(`/consolidated-business/stats${qs ? `?${qs}` : ''}`);
    },

    /** Options scoped to one company when given — every picker except the company picker. */
    getFilters(companyCode?: string): Promise<ConsBusinessFilterOptions> {
        const qs = companyCode ? `?companyCode=${encodeURIComponent(companyCode)}` : '';
        return fetchApi<ConsBusinessFilterOptions>(`/consolidated-business/filters${qs}`);
    },

    getGroup(groupKey: string, kpiMode: KpiMode): Promise<ConsBusinessGroupDetail> {
        return fetchApi<ConsBusinessGroupDetail>(
            `/consolidated-business/group/${encodeURIComponent(groupKey)}?kpiMode=${kpiMode}`,
        );
    },

    // ─── Writes (write ONLY to [Reporting]) ──────────────────────────────────
    /** The single write path since migration 132 — the four states are exclusive. */
    setStatus(groupKey: string, payload: StatusSetPayload): Promise<StatusSetResult> {
        return fetchApi<StatusSetResult>(
            `/consolidated-business/group/${encodeURIComponent(groupKey)}/status`,
            { method: 'PUT', body: JSON.stringify(payload) },
        );
    },

    bulkStatus(groupKeys: string[], payload: StatusSetPayload): Promise<{
        status: string; requested: number; skipped: number; groupCount: number; changed: number;
    }> {
        return fetchApi(`/consolidated-business/bulk-status`, {
            method: 'PUT',
            body: JSON.stringify({ groupKeys, ...payload }),
        });
    },

    getHistory(groupKey: string): Promise<ConsBusinessHistoryEvent[]> {
        return fetchApi<ConsBusinessHistoryEvent[]>(
            `/consolidated-business/group/${encodeURIComponent(groupKey)}/history`,
        );
    },

    /**
     * Invoice-level sales history of the group — the right-click drill-down. Sibling of
     * getHistory(), which returns the review audit trail rather than the money.
     *
     * Unpaged on purpose: the largest group in production has 224 invoices, the average 5.
     */
    getTransactions(groupKey: string): Promise<ConsBusinessTransactionsResponse> {
        return fetchApi<ConsBusinessTransactionsResponse>(
            `/consolidated-business/group/${encodeURIComponent(groupKey)}/transactions`,
        );
    },

    // The one-way legacy import is gone: migration 132 ran the final catch-up and pinned
    // LegacyImportEnabled to 0. defaultReviewDays now rides on the stats response.

    // ─── Comments (group- or customer-grain), shared with /customer-360 ──────
    // Always send groupKey, never an entityRef: the EntityRef hex is derived from
    // GROUP_HASH_EXPR in SQL. See api/.../consolidated-business/comments.ts.
    listComments(arm: ConsBusinessCommentArm, key: string): Promise<{ comments: ConsBusinessComment[] }> {
        const sp = new URLSearchParams({ entityType: arm });
        sp.set(arm === 'Group' ? 'groupKey' : 'customerId', key);
        return fetchApi<{ comments: ConsBusinessComment[] }>(`/consolidated-business/comments?${sp}`);
    },

    createComment(arm: ConsBusinessCommentArm, key: string, commentText: string): Promise<ConsBusinessComment> {
        const body = arm === 'Group'
            ? { entityType: arm, groupKey: key, commentText }
            : { entityType: arm, customerId: key, commentText };
        return fetchApi<ConsBusinessComment>('/consolidated-business/comments', {
            method: 'POST', body: JSON.stringify(body),
        });
    },

    updateComment(id: number, commentText: string): Promise<{ Id: number; CommentText: string }> {
        return fetchApi(`/consolidated-business/comments/${id}`, {
            method: 'PUT', body: JSON.stringify({ commentText }),
        });
    },

    /** The response matters: DeleteFailed = deleted here, but the HubSpot note survived. */
    deleteComment(id: number): Promise<{ Id: number; deleted: boolean; HubspotSyncStatus?: string | null; HubspotSyncError?: string | null }> {
        return fetchApi(`/consolidated-business/comments/${id}`, { method: 'DELETE' });
    },

    /** Retry the HubSpot note sync of one Group comment (see comments-sync-retry.ts). */
    retryCommentSync(id: number): Promise<{ Id: number; HubspotSyncStatus: string | null; HubspotNoteUrl: string | null; HubspotSyncError: string | null }> {
        return fetchApi(`/consolidated-business/comments/${id}/sync`, { method: 'POST' });
    },

    getConfig(): Promise<ConsBusinessConfig> {
        return fetchApi<ConsBusinessConfig>('/consolidated-business/config');
    },

    updateConfig(patch: Partial<ConsBusinessConfig>): Promise<ConsBusinessConfig> {
        return fetchApi<ConsBusinessConfig>('/consolidated-business/config', {
            method: 'PUT',
            body: JSON.stringify(patch),
        });
    },

    // ─── Mail: internal forward only (2026-10-07) ───────────────────────────
    /** What the forward modal needs: the customer's combinations and the employee list. */
    composeForward(customerId: string, groupKey?: string | null): Promise<ForwardComposeData> {
        const sp = new URLSearchParams({ customerId });
        if (groupKey) sp.set('groupKey', groupKey);
        return fetchApi<ForwardComposeData>(`/consolidated-business/email/forward/compose?${sp}`);
    },

    /** Recipients must be active employees — the server rejects anyone else. */
    sendForward(payload: ForwardSendPayload): Promise<MailSendResult> {
        return fetchApi<MailSendResult>('/consolidated-business/email/forward', {
            method: 'POST', body: JSON.stringify(payload),
        });
    },

    /** The mails that covered ONE combination — everyone's, not just the caller's. */
    mailHistory(groupKey: string): Promise<{ items: MailHistoryItem[] }> {
        return fetchApi<{ items: MailHistoryItem[] }>(
            `/consolidated-business/email/history?groupKey=${encodeURIComponent(groupKey)}`);
    },
};
